import { useRef } from "react";
import { motion } from "framer-motion";
import { useListLeads, getListLeadsQueryKey } from "@workspace/api-client-react";
import { useTrashCan, DRAG_TYPE, TRASH_CAN_ATTR } from "@/lib/trash-context";
import { PaperBall } from "./CrumpledPaper";
import { CompletedJobsDrawer } from "./CompletedJobsDrawer";
import { cn } from "@/lib/utils";

const BALL_CONFIGS = [
  { rotation: 12, offsetX: -8, size: 11 },
  { rotation: -22, offsetX: 5, size: 10 },
  { rotation: 38, offsetX: -2, size: 12 },
  { rotation: -14, offsetX: 10, size: 9 },
  { rotation: 51, offsetX: -6, size: 10 },
];

const COUNT_PARAMS = { status: "completed", limit: 1 } as const;

export function TrashCan() {
  const { isDragOver, setIsDragOver, drawerOpen, setDrawerOpen, isBouncing } = useTrashCan();
  const dragCounterRef = useRef(0);

  const { data: completedData } = useListLeads(COUNT_PARAMS, {
    query: { queryKey: getListLeadsQueryKey(COUNT_PARAMS) },
  });
  const completedCount = completedData?.total ?? 0;
  const ballsToShow = Math.min(completedCount, 5);

  const handleDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const raw = e.dataTransfer.getData(DRAG_TYPE);
    if (!raw) return;
    const event = new CustomEvent("trash:drop", { detail: { data: raw } });
    window.dispatchEvent(event);
  };

  return (
    <>
      <div
        {...{ [TRASH_CAN_ATTR]: "true" }}
        className={cn(
          "relative flex flex-col items-center cursor-pointer select-none mx-auto",
          "transition-transform duration-150",
          isDragOver && "scale-110"
        )}
        style={{ width: 56 }}
        onClick={() => setDrawerOpen(true)}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        title={`${completedCount} completed job${completedCount !== 1 ? "s" : ""}`}
        role="button"
        aria-label={`Completed jobs bin — ${completedCount} job${completedCount !== 1 ? "s" : ""}`}
      >
        {/* Completed count badge */}
        {completedCount > 0 && (
          <span className="absolute -top-1.5 -right-1 z-20 inline-flex items-center justify-center w-4.5 h-4.5 min-w-[18px] rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-bold leading-none px-1 py-0.5 tabular-nums shadow">
            {completedCount > 99 ? "99+" : completedCount}
          </span>
        )}

        {/* Paper balls peeking above can rim */}
        {ballsToShow > 0 && (
          <div
            className="absolute flex items-end justify-center gap-0.5 z-10 pointer-events-none"
            style={{ top: 18, left: 0, right: 0 }}
          >
            {BALL_CONFIGS.slice(0, ballsToShow).map((b, i) => (
              <div key={i} style={{ transform: `translateX(${b.offsetX}px)` }}>
                <PaperBall size={b.size} rotation={b.rotation} />
              </div>
            ))}
          </div>
        )}

        {/* SVG trash can */}
        <motion.div
          animate={
            isBouncing
              ? { y: [0, -10, 0, -5, 0], scaleX: [1, 0.9, 1.05, 0.98, 1] }
              : isDragOver
              ? { rotate: -8 }
              : { rotate: 0, y: 0, scaleX: 1 }
          }
          transition={
            isBouncing
              ? { duration: 0.45, ease: "easeInOut" }
              : { type: "spring", stiffness: 320, damping: 20 }
          }
          style={{ originX: 0.5, originY: 1 }}
        >
          <TrashCanSvg isOpen={isDragOver} />
        </motion.div>
      </div>

      <CompletedJobsDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );
}

function TrashCanSvg({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      viewBox="0 0 56 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width={56}
      height={72}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="tc-body" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#46464f" />
          <stop offset="28%" stopColor="#606068" />
          <stop offset="72%" stopColor="#555560" />
          <stop offset="100%" stopColor="#3c3c44" />
        </linearGradient>
        <linearGradient id="tc-lid" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#3a3a44" />
          <stop offset="45%" stopColor="#5e5e6a" />
          <stop offset="100%" stopColor="#38383f" />
        </linearGradient>
        <linearGradient id="tc-lid-rim" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#30303a" />
          <stop offset="50%" stopColor="#52525c" />
          <stop offset="100%" stopColor="#2e2e36" />
        </linearGradient>
        <linearGradient id="tc-handle" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#707078" />
          <stop offset="100%" stopColor="#3a3a42" />
        </linearGradient>
        <pattern id="tc-mesh" width="5" height="5" patternUnits="userSpaceOnUse">
          <path
            d="M 5 0 L 0 0 0 5"
            fill="none"
            stroke="rgba(255,255,255,0.07)"
            strokeWidth="0.5"
          />
        </pattern>
        <clipPath id="tc-body-clip">
          <path d="M11 22 L45 22 L42 66 L14 66 Z" />
        </clipPath>
      </defs>

      {/* Handle */}
      <rect
        x="21"
        y="5"
        width="14"
        height="6"
        rx="3"
        fill="url(#tc-handle)"
        stroke="rgba(0,0,0,0.35)"
        strokeWidth="0.6"
      />
      <rect
        x="23"
        y="6.5"
        width="10"
        height="3"
        rx="1.5"
        fill="rgba(255,255,255,0.06)"
      />

      {/* Lid — tilts when open */}
      <motion.g
        animate={isOpen ? { rotate: -30, x: -6, y: -3 } : { rotate: 0, x: 0, y: 0 }}
        style={{ originX: 0, originY: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 18 }}
      >
        <rect
          x="9"
          y="11"
          width="38"
          height="8"
          rx="2"
          fill="url(#tc-lid)"
          stroke="rgba(0,0,0,0.25)"
          strokeWidth="0.6"
        />
        <rect
          x="9"
          y="17"
          width="38"
          height="2"
          rx="0"
          fill="url(#tc-lid-rim)"
        />
        <rect
          x="13"
          y="12.5"
          width="22"
          height="1.5"
          rx="0.75"
          fill="rgba(255,255,255,0.10)"
        />
      </motion.g>

      {/* Can body */}
      <path d="M11 22 L45 22 L42 66 L14 66 Z" fill="url(#tc-body)" />

      {/* Mesh overlay */}
      <path
        d="M11 22 L45 22 L42 66 L14 66 Z"
        fill="url(#tc-mesh)"
        clipPath="url(#tc-body-clip)"
      />

      {/* Horizontal rib lines */}
      {[33, 44, 55].map((y) => {
        const pct = (y - 22) / (66 - 22);
        const left = 11 + pct * 3;
        const right = 45 - pct * 3;
        return (
          <line
            key={y}
            x1={left}
            y1={y}
            x2={right}
            y2={y}
            stroke="rgba(255,255,255,0.09)"
            strokeWidth="0.8"
          />
        );
      })}

      {/* Vertical stripe */}
      <line
        x1="28"
        y1="22"
        x2="28"
        y2="66"
        stroke="rgba(255,255,255,0.05)"
        strokeWidth="0.6"
      />

      {/* Body highlight on left edge */}
      <path
        d="M11 22 L13 22 L12 54 L10.5 54 Z"
        fill="rgba(255,255,255,0.07)"
      />

      {/* Bottom of can */}
      <path
        d="M14 66 L42 66 L42 68 Q28 71 14 68 Z"
        fill="#30303a"
        stroke="rgba(0,0,0,0.3)"
        strokeWidth="0.4"
      />
    </svg>
  );
}
